import * as React from 'react';
import { PlayArrowOutlined } from '@mui/icons-material';
import { Box, CircularProgress, Tooltip, Typography, useTheme } from '@mui/material';

interface ParityBarProperties {
  copied: number;
  total: number;
  isRunning?: boolean;
  totalBlocks?: number;
  blockWidth?: string;
  blockHeight?: string;
  blockSpacing?: string;
  showLoaderWhenRunning?: boolean;
}

const ParityBar: React.FC<ParityBarProperties> = ({
  copied,
  total,
  isRunning = false,
  totalBlocks = 20,
  blockWidth = '8px',
  blockHeight = '12px',
  blockSpacing = '2px',
  showLoaderWhenRunning = true,
}) => {
  const theme = useTheme();
  const percentage = total > 0 ? (copied / total) * 100 : 0;
  const formattedPercentage = percentage.toFixed(2);
  const filledBlocks = Math.round((percentage / 100) * totalBlocks);

  const getProgressColor = () => {
    if (isRunning) return '#1976d2';
    if (percentage >= 80) return '#2e7d32';
    if (percentage >= 60) return '#388e3c';
    if (percentage >= 40) return '#43a047';
    return '#66bb6a';
  };

  const tooltipTitle = isRunning
    ? `Synchronization in progress - ${formattedPercentage}% complete`
    : `Synchronization complete - ${formattedPercentage}% of resources synchronized`;

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
        }}
      >
        {isRunning && (
          <PlayArrowOutlined
            sx={{
              color: '#1976d2',
              fontSize: '1rem',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            gap: blockSpacing,
            padding: '2px',
            backgroundColor: theme.palette.grey[200],
            borderRadius: '4px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: totalBlocks }, (_, index) => (
            <Box
              key={index}
              sx={{
                width: blockWidth,
                height: blockHeight,
                backgroundColor:
                  index < filledBlocks ? getProgressColor() : theme.palette.grey[300],
                borderRadius: '1px',
              }}
            />
          ))}
          {isRunning && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(25, 118, 210, 0.3) 50%, transparent 100%)',
                animation: 'shimmer 2s ease-in-out infinite',
              }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            minWidth: '45px',
            color: getProgressColor(),
          }}
        >
          {formattedPercentage}%
        </Typography>
        {isRunning && showLoaderWhenRunning && (
          <CircularProgress size={12} sx={{ color: '#1976d2' }} />
        )}
      </Box>
    </Tooltip>
  );
};

export default ParityBar;
